/**
 * SQL seed downloader — fired by `useDynamicMenu` in dev mode when the
 * live `/api/Menu/all` returns zero rows. Pulls the canonical seed file
 * shipped under `scripts/menu-seed.sql` so an operator can apply it to
 * the production DB without leaving the browser.
 *
 * Live Menu schema (verified 2026-05-05):
 *   id UNIQUEIDENTIFIER PK, tenantId, parentId, name, code (slug),
 *   icon, url, module, sortOrder, isVisible, isActive, isDeleted,
 *   createdDate.
 *
 * The full seed script is too large to embed inline as a template
 * literal here, so we fetch it from the dev server (Vite serves
 * everything under the project root) and trigger a download.
 *
 * In production builds we never run this — the file is checked in for
 * operators to apply manually.
 */

let downloaded = false;

async function fetchSeedSql(): Promise<string> {
  // Dev server serves project files under /@fs/ — but vite blocks paths
  // outside the workspace root by default. Easiest path is to fetch from
  // /scripts/menu-seed.sql relative to the dev server's project root.
  // If the file isn't reachable that way, fall back to the inline stub.
  try {
    const res = await fetch('/scripts/menu-seed.sql', { cache: 'no-store' });
    if (res.ok) return await res.text();
  } catch {
    // ignore — fall through to inline stub
  }
  return INLINE_STUB;
}

/**
 * Trigger a one-time browser download of the menu seed SQL. No-op outside
 * the browser, in production builds, or after the first invocation.
 */
export async function downloadMenuSeedSql(): Promise<void> {
  if (downloaded) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const isDev =
    typeof import.meta !== 'undefined' &&
    (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true;
  if (!isDev) return;

  try {
    const sql = await fetchSeedSql();
    const blob = new Blob([sql], { type: 'text/sql;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'menu-seed.sql';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    downloaded = true;
    // eslint-disable-next-line no-console
    console.warn(
      '[KSS] /api/Menu/all returned no rows. Downloaded `menu-seed.sql` so you can populate the table.'
    );
  } catch {
    // Ignore — non-fatal helper.
  }
}

/**
 * Tiny fallback if the project root isn't reachable. Operators should
 * always apply `scripts/menu-seed.sql` for the canonical full seed.
 */
const INLINE_STUB = `-- KSS HRMS — minimal menu seed.
-- For the full menu hierarchy see scripts/menu-seed.sql in the repo.
-- This stub only inserts a single Dashboard row for sanity-checking.
DECLARE @TenantId UNIQUEIDENTIFIER = NULL; -- replace before running
INSERT INTO dbo.Menu (id, tenantId, parentId, name, code, icon, url, module,
                      sortOrder, isVisible, isActive, isDeleted, createdDate)
VALUES (NEWID(), @TenantId, NULL, N'Dashboard', N'DASHBOARD',
        'dashboard', '/panel/dashboard', 'Dashboard',
        0, 1, 1, 0, SYSUTCDATETIME());
`;

// Re-export the stub for any caller that wants the literal text.
export const MENU_SEED_SQL = INLINE_STUB;
